/**
 * The Holdfast buy Rush spares (M8 plan MC-h4a; canon RD7(d), D-505). Rush spares (`rush_spares`, the career-only
 * good MC-h3 adds) come only from breaking down First Rush parts, no strategic market quotes or trades them, and
 * `sellGood('rush_spares')` stays refused everywhere. The one buyer is a market station of `rush-spares.json buyer`
 * (the Holdfast, the First Rush survivors), through this sale (mirrors `career/cores.ts takesCoresAt`):
 *
 * - **Money:** a career mint of exactly `base × premium × qty` credits (`career.json rushSpares`), counted as
 *   earned. Capped by the finite derelict parts (plan decision 13: no extra limit). No strategic stock moves and no
 *   trade reaches the worker.
 * - **Standing:** `standingGain × qty` with the buyer, clamped to −100..100. The clamp never refuses the sale (the
 *   credits are the point; a capped standing just stays capped).
 * - **Refusals** (Career untouched): no `rushSpares` block, a station that is not the buyer's market station, the
 *   goods flag off (a goods sale, `goodTradeAllowed`), a retaliation refusal (`marketRefusal` `sellComponent`, like
 *   the core hand-over), a quantity that is not a whole positive number, or more than the hold carries.
 *
 * Pure: Career in, Career out. `features/rushSpares/feature.ts` registers `rushSpares.sell` on the craft hook.
 */
import { z } from 'zod'
import rushSparesJson from '../data/rush-spares.json'
import { simFactionIdSchema, type M2Data, type StationDef, type StationId } from '../data/m2Schemas'
import { marketRefusal } from '../story/refusals'
import { EPSILON, goodTradeAllowed, removeGoods } from './ops'
import { goodT } from './parts'
import { fail, invalidAmount, type CmdResult } from './result'
import { adjustStanding } from './standing'
import type { Career } from './types'

declare module '../craft/hook' {
  interface CraftCommandMap {
    /** MC-h4a: sell `qty` whole Rush spares from the hold at the docked buyer station `stationId`. */
    'rushSpares.sell': { kind: 'rushSpares.sell'; stationId: StationId; qty: number }
  }
}

const text = z.string().min(1)

export const rushSparesFileSchema = z.strictObject({
  comment: z.string().optional(),
  /** The faction whose market stations buy Rush spares. */
  buyer: simFactionIdSchema,
  /** The player-facing name of the good (`GOOD_NAMES.rush_spares`). */
  name: text,
  text: z.strictObject({
    noService: text,
    notTraded: text,
    notInHold: text,
    wholeUnits: text,
    notDocked: text,
    rowLabel: text,
    standingLabel: text,
  }),
})
export type RushSparesFile = z.infer<typeof rushSparesFileSchema>

/** The bundled buyer and text (`src/data/rush-spares.json`). */
export const RUSH_SPARES: RushSparesFile = rushSparesFileSchema.parse(rushSparesJson)

export type RushSparesConfig = NonNullable<M2Data['career']['rushSpares']>

/** True when `station` buys Rush spares: a market station of the buyer faction. */
export function buysRushSparesAt(station: Pick<StationDef, 'faction' | 'services'>, file: RushSparesFile = RUSH_SPARES): boolean {
  return station.faction === file.buyer && station.services.includes('market')
}

/** Credits the buyer pays per spare: `base × premium`. */
export function rushSparesUnit(cfg: RushSparesConfig): number {
  return cfg.base * cfg.premium
}

/** Rush spares in the hold. */
export function rushSparesHeld(career: Career): number {
  return goodT(career.cargo.parts, 'rush_spares')
}

/**
 * Sells `qty` whole Rush spares from the hold at `station` for exactly `base × premium × qty` credits and
 * `standingGain × qty` standing with the buyer (clamped). Refuses (Career untouched) as the module doc lists.
 */
export function sellRushSpares(
  career: Career,
  data: Pick<M2Data, 'career'>,
  station: StationDef,
  qty: number,
  file: RushSparesFile = RUSH_SPARES,
): CmdResult<Career> {
  const cfg = data.career.rushSpares
  if (cfg === undefined || !buysRushSparesAt(station, file)) {
    return fail('serviceUnavailable', file.text.noService.replace('{station}', station.name), { station: station.id })
  }
  if (!goodTradeAllowed(data.career.goods.tradeEnabled, 'rush_spares', 'sell')) {
    return fail('serviceUnavailable', file.text.notTraded, { good: 'rush_spares' })
  }
  const refused = marketRefusal(career, station, 'sellComponent')
  if (refused !== null) return refused
  if (!(Number.isFinite(qty) && qty > 0)) return invalidAmount('quantity', qty)
  if (!Number.isInteger(qty)) return fail('invalidAmount', file.text.wholeUnits, { requested: qty })
  const held = rushSparesHeld(career)
  if (qty > held + EPSILON) {
    return fail('notInCargo', file.text.notInHold.replace('{held}', String(Math.floor(held + EPSILON))), { good: 'rush_spares', held, requested: qty })
  }
  const sold = removeGoods(career, 'rush_spares', qty, rushSparesUnit(cfg) * qty)
  if (!sold.ok) return sold
  const gain = cfg.standingGain * qty
  return gain > 0 ? adjustStanding(sold.value, file.buyer, gain) : sold
}
